// Contrato da tela de pré-jogo dos crash games (ENG-833). Espelha a resposta
// pública do core em GET /crash/pre-game?game_name=<...>.
//
// Duas diferenças em relação aos outros endpoints consumidos por este front,
// e as duas mudam como o adapter é escrito:
//
// 1. NÃO existe envelope `{ success, message, data, date }`. Em 2xx o payload
//    vem direto na raiz; quem diz se deu certo é o status HTTP.
// 2. A resposta já chega em camelCase (é uma rota nova, montada por Action no
//    core). Por isso o mapper aqui não traduz caixa — ele só NORMALIZA
//    (defaults, coerção numérica, descarte de item inválido).
//
// Um jogo por tela, mesmo contrato: hoje Aviator e Aviator 2.

// Whitelist do core. Pedir qualquer coisa fora dela devolve 422 — então o
// front nem chega a fazer a request (ver resolveCrashGameName).
export const CRASH_GAME_NAMES = ['aviator', 'aviator-2'] as const;

export type CrashGameName = (typeof CRASH_GAME_NAMES)[number];

// Cor da vela decidida pelo CORE (regra: blue < 2x ≤ purple < 10x ≤ pink).
// O front NUNCA recalcula a faixa a partir do multiplicador: se a régua mudar,
// ela muda num lugar só. O tipo é fechado pra que uma cor nova quebre no
// type-check ao invés de sair sem estilo na tela.
export type CrashCandleColor = 'blue' | 'purple' | 'pink';

export interface CrashGameInfo {
  // "Aviator 2" — manchete da tela.
  name: string;
  // "Spribe" — subtítulo, junto do nome.
  provider: string;
  // Chave técnica; ecoa o `game_name` pedido.
  gameName: string;
}

// Uma vela do histórico.
export interface CrashCandle {
  // O multiplicador em que a rodada estourou, 2 casas (1.05, 15.72).
  multiplier: number;
  // ISO 8601 UTC (sufixo Z). Converter pro fuso do usuário só na exibição.
  roundTime: string;
  color: CrashCandleColor;
}

// Contagem de velas por cor nos últimos 30 min. Zero é resposta LEGÍTIMA
// (janela sem vela daquela faixa), não ausência de dado — por isso números e
// não `number | null`.
export interface CrashColorCounts {
  blue: number;
  purple: number;
  pink: number;
}

// ---------------------------------------------------------------------------
// Bloco de análise.
//
// O core trocou o formato: era uma LISTA de faixas, virou um OBJETO com três
// leituras diferentes (janelas de tempo, faixas e sequências). E o gate de
// compliance deixou de ser "lista vazia" e passou a ser `analysis: null` —
// null = bloco inteiro fora da tela, sem flag no front.
// ---------------------------------------------------------------------------

// Janelas que o core publica, da mais curta pra mais longa. A ordem daqui é a
// ordem das abas na tela: a chave é string ("5m"), não minuto, porque é ela que
// volta no payload.
export const CRASH_ANALYSIS_WINDOWS = [
  '5m',
  '10m',
  '15m',
  '30m',
  '60m',
] as const;

export type CrashAnalysisWindowKey = (typeof CRASH_ANALYSIS_WINDOWS)[number];

// Faixas da distribuição, na ordem em que são exibidas. São rótulos fechados do
// core — o front não recalcula faixa a partir do multiplicador (mesma regra da
// cor da vela).
export const CRASH_DISTRIBUTION_BUCKETS = [
  '<2x',
  '2-5x',
  '5-10x',
  '10-50x',
  '50x+',
] as const;

export type CrashDistributionBucket =
  (typeof CRASH_DISTRIBUTION_BUCKETS)[number];

// Uma cor dentro de uma janela: quantas velas e quanto isso representa. `pct`
// vem PRONTO do core (inteiro, 0–100) e não é recalculado aqui — os três podem
// não somar exatamente 100 por arredondamento, e tudo bem: quem arredondou foi
// quem tem a amostra.
export interface CrashColorShare {
  count: number;
  pct: number;
}

export interface CrashWindowColors {
  blue: CrashColorShare;
  purple: CrashColorShare;
  pink: CrashColorShare;
}

export interface CrashDistributionSlice {
  bucket: CrashDistributionBucket;
  count: number;
}

// "Deu 9 rosas onde o normal seriam 5". `typical` é a régua do core pra janela;
// o front só compara os dois números.
export interface CrashVsTypical {
  actual: number;
  typical: number;
}

export interface CrashAnalysisWindow {
  // Velas na janela. 0 é legítimo (jogo parado) e some da tela como "sem
  // amostra", não como erro.
  total: number;
  colors: CrashWindowColors;
  distribution: CrashDistributionSlice[];
  // Maior multiplicador da janela. null = janela sem vela.
  biggest: number | null;
  // Média dos multiplicadores da janela. null = janela sem vela.
  avgMultiplier: number | null;
  // Hoje só as rosas têm comparativo; null quando o core não manda.
  vsTypical: {
    pink: CrashVsTypical | null;
  };
}

// Uma faixa de multiplicador ("velas de 10x ou mais"). Todo campo numérico pode
// vir null = "sem dado suficiente na amostra", que NÃO é zero: "nenhuma rodada
// desde a última" e "nunca houve uma" dizem coisas opostas.
export interface CrashRangeStat {
  // 10 = "velas de 10x ou mais".
  range: number;
  // Frequência: uma ocorrência a cada N rodadas (fracionário: 14 ou 2.1).
  perRounds: number | null;
  // Rodadas desde a última ocorrência (0 = a vela mais recente bateu).
  lastRoundsAgo: number | null;
  // Segundos desde a última ocorrência.
  lastSecondsAgo: number | null;
}

// Velas seguidas da mesma cor.
export interface CrashStreak {
  color: CrashCandleColor;
  length: number;
}

export interface CrashStreaks {
  // Sequência em andamento agora. null quando o core não manda.
  current: CrashStreak | null;
  // Maior sequência da amostra.
  record: CrashStreak | null;
}

export interface CrashAnalysis {
  // Nem toda janela precisa vir: jogo com 3 minutos de coletor tem "5m" e mais
  // nada. Por isso parcial — a tela pula a aba que não existe em vez de
  // renderizar uma janela zerada, que o jogador leria como "não deu vela
  // nenhuma".
  windows: Partial<Record<CrashAnalysisWindowKey, CrashAnalysisWindow>>;
  ranges: CrashRangeStat[];
  streaks: CrashStreaks;
}

export interface CrashPreGame {
  game: CrashGameInfo;
  // Total pago hoje. Hoje o core devolve SEMPRE null (não há fonte de payout).
  // Com null o bloco fica oculto — nunca renderizar "R$ 0", que seria uma
  // informação falsa sobre o jogo.
  paidToday: number | null;
  last30min: CrashColorCounts;
  // Máx. 48 itens (crash.pre_game.history_limit), DA MAIS RECENTE PARA A
  // MAIS ANTIGA (índice 0 = última vela). A ordem vem pronta do core e não é
  // reordenada no front.
  cycleHistory: CrashCandle[];
  // `null` enquanto o gate de compliance do core estiver fechado
  // (CRASH_ANALYSIS_ENABLED). Null = bloco oculto; é o próprio dado que serve
  // de flag, então ligar o bloco não precisa de deploy do front.
  analysis: CrashAnalysis | null;
  // Horário da vela mais recente (ISO 8601 UTC). Base do "ao vivo há Xs" e da
  // detecção de histórico velho.
  //
  // ARMADILHA: na prática ele NUNCA chega null. O core calcula
  // `$sample->first()?->round_time` (null sem rodada), mas o
  // CrashPreGameResource troca por `now()` antes de serializar. Num jogo sem
  // vela nenhuma o campo diz "agora" a cada poll — quem separa "coletor vivo"
  // de "coletor desligado" é o `cycleHistory` vazio, nunca este campo.
  updatedAt: string | null;
}
